import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { InputSwitchModule } from 'primeng/inputswitch';
import { DropdownModule } from 'primeng/dropdown';
import { PropertyService } from '../service/property.service';
import { MultiSelectModule } from 'primeng/multiselect';
import { usPhoneValidator } from 'src/app/core/shared/validators/us-phone.validator';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { PhoneUtils } from 'src/app/core/shared/utils/phone.utils';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-property-info',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    InputTextareaModule,
    InputSwitchModule,
    DropdownModule,
    MultiSelectModule,
    ToastModule,
    NgxMaskDirective
  ],
  providers: [MessageService, provideNgxMask()],
  templateUrl: './property-info.component.html',
  styleUrls: ['./property-info.component.scss'],
})
export class PropertyInfoComponent {
  @Input() property: any | null = null;
  @Input() buttonLabel: any | null = null;
  @Output() closeDialog = new EventEmitter<void>();

  propertyForm: FormGroup;
  users: any[] = [];
  isCopied = false;
  baseUrl = environment.baseUrl;
  dialogFlowAgentId = environment.dialogFlowAgentId;
  script = '';
  domain = window.location.origin;
  readonly modelUnitOptions = [
    { label: 'No', value: false },
    { label: 'Yes', value: true },
  ];

  constructor(
    private fb: FormBuilder,
    private propertyService: PropertyService,
    private messageService: MessageService,
  ) {
    this.propertyForm = this.fb.group({
      name: ['', Validators.required],
      address: ['', Validators.required],
      description: ['', Validators.required],
      timezone: ['', Validators.required],
      domain: ['', Validators.required],
      phone: ['', [Validators.required, usPhoneValidator]], // US format xxx-xxx-xxxx
      chatbot_script: ['', Validators.required],
      faq: [''],
      email: [{ value: '', disabled: true }, Validators.email],
      emailAppPassword: [{ value: '', disabled: true }],
      activeTwilioNumber: ['', usPhoneValidator],
      bufferTime: [4, [Validators.required, Validators.min(1), Validators.max(12), this.halfStepValidator]],
      preBookConfirmationHours: [null, [Validators.min(1)]],
      preBookCancelHours: [null, [Validators.min(1)]],
      modelUnit: [false],
      residentFilteringEnabled: [false],
      yardi_guest_card_pull: [false],
      yardi_guest_card_push: [false],
      selfGuidedTour: this.fb.group({
        enabled:             [false],
        title:               ['', [Validators.maxLength(50), PropertyInfoComponent.noSpecialCharsValidator]],
        description:         ['', [Validators.maxLength(256), PropertyInfoComponent.noSpecialCharsValidator]],
        externalBookingUrl:  [''],
        internalTitle:       ['', [Validators.maxLength(50), PropertyInfoComponent.noSpecialCharsValidator]],
        internalDescription: ['', [Validators.maxLength(256), PropertyInfoComponent.noSpecialCharsValidator]],
      }),
    });
  }

  ngOnInit() {
    this.getPropertyInfo();

    // If property is passed as input, patch form
    if (this.property) {
      this.patchForm(this.property);
    }

    // Dynamically add/remove validators when self-guided is toggled
    this.selfGuidedForm.get('enabled')!.valueChanges.subscribe(enabled => {
      this.updateSelfGuidedValidators(enabled);
    });
  }

  get selfGuidedForm(): FormGroup {
    return this.propertyForm.get('selfGuidedTour') as FormGroup;
  }

  private static noSpecialCharsValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    return /[#$%^&*@~`+|<>{}\[\]\\]/.test(control.value) ? { specialChars: true } : null;
  }

  private static urlValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    try {
      const url = new URL(control.value);
      const hostname = url.hostname.toLowerCase();
      if (!['http:', 'https:'].includes(url.protocol)) return { invalidUrl: true };
      if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') return { invalidUrl: true };
      if (!hostname.includes('.')) return { invalidUrl: true };
      return null;
    } catch {
      return { invalidUrl: true };
    }
  }

  private updateSelfGuidedValidators(enabled: boolean): void {
    const nc = PropertyInfoComponent.noSpecialCharsValidator;
    const textFields: Record<string, any[]> = {
      title:               [Validators.maxLength(50), nc],
      description:         [Validators.maxLength(256), nc],
      internalTitle:       [Validators.maxLength(50), nc],
      internalDescription: [Validators.maxLength(256), nc],
      externalBookingUrl:  [PropertyInfoComponent.urlValidator],
    };
    Object.entries(textFields).forEach(([field, baseValidators]) => {
      const ctrl = this.selfGuidedForm.get(field);
      if (!ctrl) return;
      ctrl.setValidators(enabled ? [Validators.required, ...baseValidators] : baseValidators);
      ctrl.updateValueAndValidity({ emitEvent: false });
    });
  }

  halfStepValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value == null || value === '') return null; // allow empty
    return (value * 2) % 1 === 0 ? null : { halfStep: true };
  }

  /** Fetch property info from API */
  getPropertyInfo() {
    this.propertyService.getPropertyInfo().subscribe({
      next: (res) => {
        let phone = res.phone || '';
        let activeTwilioNumber = res.activeTwilioNumber || '';

        // Remove +1 if present
        if (phone && phone.startsWith('1')) {
          res.phone = phone.substring(1); // cut off 1
        } else if (phone.startsWith('+1')) {
          res.phone = phone.substring(2); // cut off +1
        }

        if (activeTwilioNumber && activeTwilioNumber.startsWith('1')) {
          res.activeTwilioNumber = activeTwilioNumber.substring(1); // cut off 1
        } else if (activeTwilioNumber.startsWith('+1')) {
          res.activeTwilioNumber = activeTwilioNumber.substring(2); // cut off +1
        }

        this.property = res;
        if (res) {
          this.patchForm(res);
        }
      },
      error: (err) => console.error('Error fetching property info', err),
    });
  }

  /** Patch form with property data */
  private patchForm(property: any) {

    this.script = `<script src="${this.domain}/js/leasing-widget.js" data-property-id="${property._id}" data-mode="production"></script>`;

    this.propertyForm.patchValue({
      name: property.name,
      address: property.address,
      description: property.description,
      timezone: property.timezone,
      domain: property.domain,
      phone: property.phone,
      chatbot_script: this.script,
      faq: property.faq,
      email: property.email,
      emailAppPassword: property.emailAppPassword,
      bufferTime: property?.bufferTime ?? 4,
      preBookConfirmationHours: property?.preBookConfirmationHours ?? null,
      preBookCancelHours: property?.preBookCancelHours ?? null,
      modelUnit: property?.modelUnit ?? false,
      residentFilteringEnabled: property?.residentFilteringEnabled ?? false,
      activeTwilioNumber: property?.activeTwilioNumber ?? '',
      yardi_guest_card_pull: property?.yardi_guest_card_pull ?? false,
      yardi_guest_card_push: property?.yardi_guest_card_push ?? false,
    });
    // Patch nested group directly — avoids propagation issues with parent patchValue
    const sgtEnabled = property?.selfGuidedTour?.enabled ?? false;
    this.updateSelfGuidedValidators(sgtEnabled);
    this.selfGuidedForm.patchValue({
      enabled:             property?.selfGuidedTour?.enabled             ?? false,
      title:               property?.selfGuidedTour?.title               ?? '',
      description:         property?.selfGuidedTour?.description         ?? '',
      externalBookingUrl:  property?.selfGuidedTour?.externalBookingUrl  ?? '',
      internalTitle:       property?.selfGuidedTour?.internalTitle       ?? '',
      internalDescription: property?.selfGuidedTour?.internalDescription ?? '',
    });
  }

  /** Update Property Info */
  saveProperty() {
    if (this.propertyForm.valid) {
      const propertyData = this.propertyForm.getRawValue(); // includes disabled fields (email, emailAppPassword)
      if (this.property?._id) {
        // Update existing property
        //Add +1 prefix and remove dashes for US number
        propertyData.phone = PhoneUtils.toE164(propertyData.phone);
        if (propertyData.activeTwilioNumber) {
          propertyData.activeTwilioNumber = PhoneUtils.toE164(propertyData.activeTwilioNumber);
        }
        
        //console.log('Updating property with data:', propertyData);
        this.propertyService.updatePropertyInfo(propertyData).subscribe({
          next: (res) => {
            let phone = res.phone || '';
            let activeTwilioNumber = res.activeTwilioNumber || '';
            // Remove +1 if present
            if (phone && phone.startsWith('1')) {
              res.phone = phone.substring(1); // cut off 1
            } else if (phone.startsWith('+1')) {
              res.phone = phone.substring(2); // cut off +1
            }

            if (activeTwilioNumber && activeTwilioNumber.startsWith('1')) {
              res.activeTwilioNumber = activeTwilioNumber.substring(1); // cut off 1
            } else if (activeTwilioNumber.startsWith('+1')) {
              res.activeTwilioNumber = activeTwilioNumber.substring(2); // cut off +1
            }

            this.property = res; // keep latest info
            this.patchForm(res); // refresh form with updated values
            this.closeDialog.emit(); // close dialog if needed
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Property Info Saved successfully!',
            });
          },
          error: (err) => {
            const errorMessage = err?.error?.message || 'Failed to update Property Info!';
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: errorMessage,
            });
          },
        });
      } else {
        console.warn('No property ID found for update.');
      }
    }
  }


  onYardiToggleChange(field: 'yardi_guest_card_pull' | 'yardi_guest_card_push', value: boolean) {
    this.propertyService.updatePropertyInfo({ [field]: value }).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Yardi ${field === 'yardi_guest_card_pull' ? 'Guest Card Pull' : 'Guest Card Push'} ${value ? 'enabled' : 'disabled'}.`,
        });
      },
      error: (err) => {
        const errorMessage = err?.error?.message || 'Failed to update Yardi toggle.';
        this.messageService.add({ severity: 'error', summary: 'Error', detail: errorMessage });
        this.propertyForm.get(field)?.setValue(!value, { emitEvent: false });
      },
    });
  }

  copyCode() {
    const code = this.propertyForm.get('chatbot_script')?.value;
    if (code) {
      navigator.clipboard.writeText(code).then(() => {
        this.isCopied = true;
        setTimeout(() => {
          this.isCopied = false;
        }, 2000);
      });
    }
  }

}
